import {
  IChoiceView,
  IInstanceReportField,
  IMultipleChoiceQuestionView,
  IQuestionReportField,
  IQuestionsView,
  IReportField,
  IScoreRangeReportField,
  ISeries,
  ISurveyView,
  ITagReportField,
  Metric,
} from 'store/survey/surveyTypes';
import { isANumber, roundDecimal } from 'utils/number';
import { IQueryBucket } from 'store/entity/IQueryBucket';
import { ITags, ITagView } from 'store/providers/providerTypes';
import { IQueryFilter } from 'store/entity/IQueryFilter';
import { NumberOrNull } from 'store/entity/types';
import { responseListQuestionFields } from './SeriesEditor';

export const roundToDynamicFloating = (value: number) => {
  const abs = Math.abs(value);
  if (abs < 0.1) {
    return roundDecimal(6)(value);
  } else if (abs > 0.1 && abs < 10) {
    return roundDecimal(2)(value);
  } else {
    return roundDecimal(1)(value);
  }
};
export interface ITagValues {
  tag: number;
  child: boolean;
}

export const calculatePercentage = (
  value: number,
  target?: number,
  baseLine?: number
): number | undefined => {
  if (
    typeof target === 'undefined' ||
    typeof baseLine === 'undefined' ||
    (target === 0 && baseLine === 0)
  ) {
    return undefined;
  }
  if (target === baseLine) return undefined;
  // if (target === 0 && baseLine !== 0) {
  return ((value - baseLine) / (target - baseLine)) * 100;
  // }
  // if (target >= baseLine) {
  //   return roundToDynamicFloating(value / target) * 100;
  // }
  // return roundToDynamicFloating((2 * target - value) / target) * 100;
};

export const makeBucket = (
  list: Array<
    ITagReportField | IQuestionReportField | IInstanceReportField | ISeries
  >,
  survey: ISurveyView | undefined,
  context: any
): IQueryBucket | undefined => {
  if (list.length > 0) {
    const firstItem = list.shift();
    const subBucket = makeBucket(list, survey, context);
    if (firstItem && (firstItem as any).metric) {
      // item is a series
      const seriesItem = firstItem as ISeries;
      if (
        seriesItem.questionId &&
        seriesItem.questionId !== '0' &&
        !seriesItem.questionId?.toString().includes(',') &&
        survey?.questionMap?.[seriesItem.questionId!]?.data?.type ===
          'multipleAnswer' /*question is multiple answer*/
      ) {
        let ret: IQueryBucket | undefined = subBucket;
        if (seriesItem.splitOnValues && seriesItem.subQuestionId) {
          ret = {
            name: 'TermByFieldValue',
            type: 'bucket',
            field: 'value',
            subBuckets: ret ? [ret] : [],
            includeTotal: false,
            customField: {
              field: seriesItem.subQuestionId,
              questionId: seriesItem.questionId,
            },
          };
        }
        if (seriesItem.splitOnFieldValues) {
          ret = {
            name: 'TermByFieldValue',
            type: 'bucket',
            field: 'allAnswers',
            prefix: `${seriesItem.questionId}$$${seriesItem.splitOnFieldValues}`,
            subBuckets: ret ? [ret] : [],
            includeTotal: false,
            customField: {
              field: seriesItem.splitOnFieldValues,
              questionId: seriesItem.questionId,
            },
          };
        }
        return ret!;
      }
      // split on tag
      if ((seriesItem.split && seriesItem.tagIds?.length) || 0 > 0) {
        return {
          name: 'TermByTag',
          type: 'bucket',
          field: 'q_tags',
          subBuckets: subBucket ? [subBucket] : [],
          includeTotal:
            seriesItem.includeTotal && (seriesItem.tagIds?.length ?? 0) > 1,
          validBuckets: seriesItem.tagIds,
        };
      }
      const splitOnAllQuestions =
        seriesItem.questionId === '0' ||
        seriesItem.questionId?.toString().includes(',');
      const bucket: IQueryBucket = {
        name: splitOnAllQuestions
          ? 'TermByAllQuestions'
          : 'TermByAllSubQuestions',
        type: 'bucket',
        subBuckets: subBucket ? [subBucket] : [],
        includeTotal: seriesItem.includeTotal, // !splitOnAllQuestions,
        field: !survey
          ? 'sqId'
          : splitOnAllQuestions
            ? 'questionId'
            : 'subQuestionId',
      };
      if (
        seriesItem.questionId !== '0' &&
        !seriesItem.questionId?.toString().includes(',')
      ) {
        bucket.customField = seriesItem.questionId;
      }
      if (
        seriesItem.splitOnValues &&
        (seriesItem.subQuestionId === '' ||
          isANumber(Number(seriesItem.questionId)))
      ) {
        const splitValueBucket: IQueryBucket = {
          name: 'TermByValue',
          type: 'bucket',
          field: 'value',
          subBuckets: undefined, // for percentage of count
          includeTotal: seriesItem.metric === 'percentageOfCount', // for percentage of count
          customField: seriesItem.questionId,
        };
        if (bucket && seriesItem.subQuestionId === '') {
          bucket.subBuckets = [splitValueBucket];
          return bucket;
        }
        return splitValueBucket;
      } else return bucket;
    } else {
      // item is a field
      if (firstItem && firstItem.type === 'question') {
        const questionField = firstItem as IQuestionReportField;
        const bucket: IQueryBucket = {
          name: 'TermByAnswers',
          type: 'bucket',
          subBuckets: subBucket ? [subBucket] : [],
          field: 'allAnswers',
          includeTotal: (firstItem as IReportField).includeTotal,
          hideMissingData: (firstItem as IReportField).hideMissingData,
          depth: (firstItem as IReportField)?.depth,
        };
        bucket.prefix = `${questionField.questionId}`;
        if (questionField.subQuestionId) {
          bucket.prefix = `${bucket.prefix}$$${questionField.subQuestionId}`;
        }
        return bucket;
      } else if (firstItem && firstItem.type === 'tag') {
        const tagField = firstItem as ITagReportField;
        const bucket: IQueryBucket = {
          name: 'TermByTag',
          type: 'bucket',
          subBuckets: subBucket ? [subBucket] : [],
          field: 'tags',
          includeTotal: (firstItem as IReportField).includeTotal,
          hideMissingData: (firstItem as IReportField).hideMissingData,
        };
        const idList = tagField.values.reduce(
          (tagIdList: Set<number>, value: ITagValues) => {
            if (value.child) {
              (
                Object.values((context.tags?.data as any) || {}) as ITagView[]
              ).forEach((tag: ITagView) => {
                if (tag.parentId === value.tag && !tag.deleted) {
                  tagIdList.add(Number(tag.id));
                }
              });
            } else {
              tagIdList.add(value.tag);
            }
            return tagIdList;
          },
          new Set()
        );
        bucket.validBuckets = Array.from(idList);
        return bucket;
      } else if (firstItem && firstItem.type === 'instance') {
        const instanceField = firstItem as IInstanceReportField;
        const bucket: IQueryBucket = {
          name: 'TermByInstance' + firstItem.instanceType,
          type: 'bucket',
          subBuckets: subBucket ? [subBucket] : [],
          field: 'instance',
          customField: firstItem.instanceType,
          includeTotal: (firstItem as IReportField).includeTotal,
          hideMissingData: (firstItem as IReportField).hideMissingData,
        };
        if (instanceField.values.length > 0) {
          bucket.validBuckets = instanceField.values;
        }
        return bucket;
      }
    }
  }
  return;
};

export const makeFiltersFromSeries = (
  seriesState: ISeries,
  survey?: ISurveyView
): IQueryFilter[] => {
  // todo : how to determin the query is on multipleAnswerWithFields
  const list: IQueryFilter[] = [];
  if (seriesState.type === 'question') {
    if (seriesState.valueFilter?.length) {
      list.push({
        type: 'termFilter',
        field: 'value',
        values: seriesState.valueFilter.map((it) => it.replace(' ', '_')),
      });
    }
    if (
      seriesState.questionId !== '0' &&
      !seriesState.questionId?.toString().includes(',')
    ) {
      const question =
        survey?.questionMap?.[Number(seriesState.questionId)]?.data;

      if (question?.type === 'multipleAnswer') {
        let subQuestionFilter: IQueryFilter;
        if (seriesState.subQuestionId)
          subQuestionFilter = {
            type: 'termFilter',
            field: 'field',
            values: [seriesState.subQuestionId],
          };
        else {
          subQuestionFilter = {
            type: 'doubleRangeFilter',
            field: 'field',
          };
        }
        list.push(subQuestionFilter);
      } else if (seriesState.subQuestionId) {
        const subQuestionFilter: IQueryFilter = {
          type: 'termFilter',
          field: 'subQuestionId',
          values: [seriesState.subQuestionId],
        };
        list.push(subQuestionFilter);
      }
    }
    if (survey) {
      const questionFilter: IQueryFilter = {
        type: 'termFilter',
        field: 'questionId',
        values:
          seriesState.questionId !== '0'
            ? (seriesState.questionId
                ?.toString()
                .split(',')
                .filter((el) => !responseListQuestionFields.includes(el))
                .map((el) => {
                  if (el.includes('@score')) {
                    return el.split('@score').at(0) as string;
                  } else return el;
                })
                .map((i) => Number(i)) ?? [])
            : [],
      };
      list.push(questionFilter);
    }
  } else if (seriesState.type === 'tag') {
    const values = seriesState.tagId ? [seriesState.tagId] : [];
    if (seriesState.tagIds) {
      values.push(...seriesState.tagIds);
    }
    const tagFilter: IQueryFilter = {
      type: 'termFilter',
      field: 'q_tags',
      values: values,
    };
    list.push(tagFilter);
  }

  return list;
};

export const makeFiltersFromFields = (
  fieldsState: Array<IReportField>,
  tags: ITags
): IQueryFilter[] => {
  return fieldsState.reduce(
    (filterList: IQueryFilter[], field: IReportField) => {
      if (field.type === 'question') {
        const questionField = field as IQuestionReportField;
        if (questionField.choiceIds?.length > 0) {
          const values = questionField.choiceIds.reduce(
            (val: string[], choice: string) => {
              if (questionField.subQuestionId) {
                val.push(
                  `${questionField.questionId}$$${questionField.subQuestionId}$$${choice}`
                );
              } else {
                val.push(`${questionField.questionId}$$${choice}`);
              }
              return val;
            },
            []
          );
          const item: IQueryFilter = {
            type: 'termFilter',
            field: 'allAnswers',
            values,
          };
          filterList.push(item);
        }
      } else if (field.type === 'tag') {
        const tagField = field as ITagReportField;
        const idList = tagField.values.reduce(
          (valueList: Set<number>, value) => {
            if (value.child) {
              Object.values(tags.data || {}).forEach((tag) => {
                if (tag.parentId === value.tag) {
                  valueList.add(Number(tag.id));
                }
              });
            } else {
              valueList.add(value.tag);
            }
            return valueList;
          },
          new Set<number>()
        );
        const values = Array.from(idList);
        const item: IQueryFilter = {
          type: 'termFilter',
          field: 'tags',
          values,
        };
        filterList.push(item);
      } else if (field.type === 'instance') {
        const instanceField = field as IInstanceReportField;
        if (instanceField.values.length > 0) {
          const item: IQueryFilter = {
            type: 'termFilter',
            field: 'instance',
            values: instanceField.values,
          };
          filterList.push(item);
        }
      } else if (field.type === 'score') {
        const scoreRangeFilter = field as IScoreRangeReportField;
        const items: Array<IQueryFilter> = [
          {
            type: 'rangeFilter',
            field: 'score',
            start: Number(scoreRangeFilter?.min),
            end: Number(scoreRangeFilter?.max),
          },
          ...(scoreRangeFilter?.questionId
            ? [
                {
                  type: 'termFilter',
                  field: 'questionId',
                  values: [scoreRangeFilter.questionId?.toString()],
                },
              ]
            : ([] as any)),
        ];
        filterList.push(...items);
      }
      return filterList;
    },
    []
  );
};

export const calculateValue = (value: number): number => {
  return roundToDynamicFloating(value);
};

export const maxScoreOfQuestion = (
  question: IMultipleChoiceQuestionView
): number => {
  return question.choiceList.reduce((maxScore: number, choice: IChoiceView) => {
    if (choice.score && maxScore < choice.score) {
      return choice.score;
    }
    return maxScore;
  }, 1);
};

export const calculateMaxScore = (
  questions: IQuestionsView,
  series: ISeries,
  surveyMaxScore?: NumberOrNull,
  thisQuestionId?: number | string
): number => {
  if (series.type === 'all' || series.type === 'tag') {
    return surveyMaxScore || 0;
  }
  let question = questions[Number(series.questionId)];
  if (series.questionId === '0') {
    question = questions[Number(thisQuestionId)];
  }
  const questionType = question.type;
  if (questionType === 'multipleChoice') {
    return maxScoreOfQuestion(question);
  } else if (questionType === 'grid') {
    const maxScoreOfChoices = maxScoreOfQuestion(
      question as IMultipleChoiceQuestionView
    );
    return maxScoreOfChoices;
  }
  return 1;
};

export const addScoreToMetric = (metric: Metric): string => {
  return `${metric}Score`;
};
